import {
  useGetMillwrightDaysSinceLastFatigueWarningQuery,
  useGetMillwrightDaysSinceLastIncidentQuery,
} from '@/apis/incidents/incidentsApi';
import { FC } from 'react';
import { GradientBar } from '@/components';
import {
  Icon,
  LineChart,
  LoadingSpinner,
  Stack,
  Tooltip,
  Typography,
} from '@data-products-and-ai/react-components';
import { SPINNER_SIZE } from '@/constants';
import {
  MillwrightLastAccident,
  MillwrightLastFatigueWarning,
} from '@/pages/Millwrights/[ID]/components';
import { kpisRanges } from '@/pages/Millwrights/[ID]/components/MillwrightProfile/kpisData';
import { useGetMillwrightRiskTimelineByOutageIdQuery } from '@/apis/risks/risksApi';
import { useGetMillwrightKPIsQuery } from '@/apis/millwrights/millwrightsApi';
import { RangeColor } from '@/types';

import '@/pages/Millwrights/[ID]/components/MillwrightProfile/MillwrightProfile.scss';

const EXCLUDED_NAMES = [
  'Age',
  'Other Skills Score',
  'Safety Skills Score',
  'Average Evaluation Score',
  'Median Evaluation Score',
  'Evaluation Score Variability',
  'Unique Evaluation Count',
  'Warning Count',
  'Injuries (Last 6 Months)',
  'Avg Evaluation Score (Last 6 Months)',
];

const NUMBER_OF_KPIS = 4;

const TIMELINE_TOOLTIP_INFO = `This chart displays the worker’s injury risk score trend over the past six months, based on key factors such as age and worked hours.

Each point on the line represents the calculated risk score for that period. Changes in the trend may reflect variations in workload, overtime, double-time hours, or recovery time.

Injury events, if any occurred during this period, are marked directly on the timeline. These indicators show when an injury was recorded, helping correlate risk score changes with real incidents.

Use this chart to identify rising risk patterns, periods of elevated strain, and potential early warning signs before injuries occur.`;

interface MillwrightProfileProps {
  millwrightId: number;
  outageId?: number;
}

export const MillwrightProfile: FC<MillwrightProfileProps> = ({
  millwrightId,
  outageId,
}) => {
  const { data: daysSinceLastIncident, isLoading: isDaysSinceIncidentLoading } =
    useGetMillwrightDaysSinceLastIncidentQuery(millwrightId, {
      skip: !millwrightId,
    });
  const {
    data: daysSinceLastWarning,
    isLoading: isDaysSinceLastWarningLoading,
  } = useGetMillwrightDaysSinceLastFatigueWarningQuery(millwrightId, {
    skip: !millwrightId,
  });
  const { data: riskTimeline, isLoading: isRiskTimelineLoading } =
    useGetMillwrightRiskTimelineByOutageIdQuery(
      {
        empId: millwrightId,
        months: 6,
      },
      { skip: !millwrightId },
    );

  const { data: kpisData, isLoading: isKpisLoading } =
    useGetMillwrightKPIsQuery({
      empId: millwrightId,
      outageId: outageId ?? null,
      topN: NUMBER_OF_KPIS,
      excludeNames: EXCLUDED_NAMES,
    });

  const isLoading =
    isDaysSinceIncidentLoading ||
    isDaysSinceLastWarningLoading ||
    isRiskTimelineLoading ||
    isKpisLoading;

  return (
    <div className="millwright-profile">
      <Stack gap={48}>
        <Typography color="var(--color-black)" tag="h3">
          Millwright Profile
        </Typography>
        {isLoading ? (
          <LoadingSpinner
            text="Loading millwright profile..."
            size={SPINNER_SIZE}
            color="var(--color-primary)"
            align="center"
            tag="textdefault"
            inline
          />
        ) : (
          <>
            <div className="millwright-profile__data">
              <div className="millwright-profile__bars">
                {kpisData?.map((kpi) => (
                  <GradientBar
                    value={
                      Math.round((+kpi.kpiValue + Number.EPSILON) * 100) / 100
                    }
                    label={kpi.kpiName}
                    data={kpisRanges[Number(kpi.kpiId) - 1] as RangeColor[]}
                    description={kpi.kpiDescription}
                  />
                ))}
              </div>
              <div className="millwright-profile__risk-timeline">
                <Stack direction="row" gap={8}>
                  <Typography
                    tag="textdefault_strong"
                    color="var(--color-neutral)"
                  >
                    Risk score timeline (6m)
                  </Typography>
                  <Tooltip title={TIMELINE_TOOLTIP_INFO} arrow>
                    <Icon
                      icon="IconInformation"
                      color="var(--color-default)"
                      iconSize={0.8}
                    />
                  </Tooltip>
                </Stack>

                <LineChart
                  data={
                    riskTimeline?.points.map((item) => ({
                      ...item,
                      name: item.date,
                    })) ?? []
                  }
                  categoryKey="name"
                  chartKeys={['riskScore', 'injury']}
                  hasAnimation
                  hasGrid
                  hasTooltip
                  hasBrush={false}
                  legendType="none"
                  height={420}
                />
              </div>
            </div>
            <div className="millwright-profile__incidents">
              <MillwrightLastAccident data={daysSinceLastIncident} />
              <MillwrightLastFatigueWarning data={daysSinceLastWarning} />
            </div>
          </>
        )}
      </Stack>
    </div>
  );
};
